import { types } from "mobx-state-tree";
import { allProducts } from "helpers/fakeData";
import { uuidv4 } from "helpers/uuid";
import { safeLocalStorage } from "helpers/localStorage";

export interface CartItem {
  productId: string;
  size?: string;
  color?: string;
  quantity: number;
}

type CartItemWithoutQuantity = Omit<CartItem, "quantity">;
export function getCartItemKey(item: CartItemWithoutQuantity) {
  return `${item.productId}-${item.size}-${item.color}`;
}

export function getCartTotal(cart: CartItem[]) {
  return cart.reduce((acc, item) => {
    const product = allProducts.find((p) => p.id === item.productId);
    if (product == null) {
      return acc;
    }
    return acc + item.quantity * product.price;
  }, 0);
}

function saveCartToLocalStorage(cartId: string, cart: CartItem[]) {
  safeLocalStorage.setItem("cartId", cartId);
  safeLocalStorage.setItem(cartId, JSON.stringify(cart));
}

const CartItem = types.model("CartItem", {
  productId: types.string,
  size: types.maybe(types.string),
  color: types.maybe(types.string),
  quantity: types.number,
});

export const MarketPlaceStore = types
  .model("MarketPlaceStore", {
    selectedProductId: types.maybe(types.string),
    cart: types.optional(types.array(CartItem), []),
    cartId: types.maybe(types.string),
  })
  .actions((self) => ({
    setCartId(cartId: string) {
      self.cartId = cartId;
    },
    setSelectedProductId(productId: string) {
      self.selectedProductId = productId;
    },
    addToCart(item: CartItem) {
      if (item.quantity === 0) {
        return;
      }
      const key = getCartItemKey(item);
      const existingItem = self.cart.find((i) => getCartItemKey(i) === key);
      if (existingItem) {
        existingItem.quantity = item.quantity;
      } else {
        self.cart.push(item);
      }
      if (self.cartId == null) {
        self.cartId = uuidv4();
      }
      saveCartToLocalStorage(self.cartId, self.cart);
    },
    removeFromCart(item: CartItem) {
      const key = getCartItemKey(item);
      self.cart.replace(self.cart.filter((i) => getCartItemKey(i) !== key));
      if (self.cartId == null) {
        self.cartId = uuidv4();
      }
      saveCartToLocalStorage(self.cartId, self.cart);
    },
    clearCart(cartId?: string) {
      self.cart.clear();
      const id = cartId ?? self.cartId;
      if (id != null) {
        safeLocalStorage.removeItem("cartId");
        safeLocalStorage.removeItem(id);
      }
    },
  }))
  .views((self) => ({
    get cartTotal() {
      return getCartTotal(self.cart);
    },
  }));

export const createMarketPlaceStore = () => {
  return MarketPlaceStore.create({});
};
export type TMarketPlaceStore = ReturnType<typeof createMarketPlaceStore>;
